import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Wallet, TrendingUp, Send, CreditCard, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { walletAPI, transactionsAPI } from '../services/api';
import { formatCurrency, formatDate, CRYPTO_SYMBOLS } from '../utils/constants';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Dashboard = () => {
  const [balances, setBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [balancesRes, transactionsRes] = await Promise.all([
        walletAPI.getBalances(),
        transactionsAPI.getHistory()
      ]);
      setBalances(balancesRes.data);
      setTransactions(transactionsRes.data.slice(0, 5));
    } catch (error) {
      toast.error(error.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const totalValueAED = balances.reduce((sum, b) => sum + b.value_aed, 0);
  const aedBalance = balances.find(b => b.crypto === 'AED')?.balance || 0;

  // Mock chart data
  const chartData = [
    { date: 'Jan', value: totalValueAED * 0.7 },
    { date: 'Feb', value: totalValueAED * 0.8 },
    { date: 'Mar', value: totalValueAED * 0.75 },
    { date: 'Apr', value: totalValueAED * 0.9 },
    { date: 'May', value: totalValueAED * 0.95 },
    { date: 'Jun', value: totalValueAED }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-white text-xl font-secondary">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-primary font-bold text-white mb-2">WELCOME BACK</h1>
          <p className="text-gray-400 font-secondary text-lg">{user?.full_name}</p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="glass-card p-6 rounded-2xl"
            data-testid="total-portfolio-card"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-400 font-secondary text-sm uppercase tracking-wider">Total Portfolio</span>
              <div className="w-10 h-10 rounded-full bg-[#7000FF]/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#7000FF]" />
              </div>
            </div>
            <div className="text-3xl font-mono font-bold text-white">{formatCurrency(totalValueAED)}</div>
            <div className="flex items-center gap-1 mt-2 text-[#00FF94] text-sm font-secondary">
              <ArrowUpRight className="w-4 h-4" />
              <span>+12.5%</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="glass-card p-6 rounded-2xl"
            data-testid="aed-balance-card"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-400 font-secondary text-sm uppercase tracking-wider">AED Balance</span>
              <div className="w-10 h-10 rounded-full bg-[#00F0FF]/20 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-[#00F0FF]" />
              </div>
            </div>
            <div className="text-3xl font-mono font-bold text-white">{formatCurrency(aedBalance)}</div>
            <Link to="/convert" className="text-[#00F0FF] text-sm font-secondary mt-2 inline-block hover:text-[#00C2CC] transition-colors">
              Convert crypto →
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="glass-card p-6 rounded-2xl"
            data-testid="crypto-assets-card"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-400 font-secondary text-sm uppercase tracking-wider">Crypto Assets</span>
              <div className="w-10 h-10 rounded-full bg-[#FF0080]/20 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-[#FF0080]" />
              </div>
            </div>
            <div className="text-3xl font-mono font-bold text-white">{balances.filter(b => b.crypto !== 'AED' && b.balance > 0).length}</div>
            <Link to="/wallet" className="text-[#FF0080] text-sm font-secondary mt-2 inline-block hover:text-[#CC0066] transition-colors">
              View wallet →
            </Link>
          </motion.div>
        </div>

        {/* Chart Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="glass-card p-6 rounded-2xl"
        >
          <h2 className="text-xl font-primary font-bold text-white mb-6">PORTFOLIO VALUE</h2>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7000FF" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#7000FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="#666" style={{ fontSize: '12px' }} />
              <YAxis stroke="#666" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(15, 10, 40, 0.9)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  color: '#fff'
                }}
              />
              <Area type="monotone" dataKey="value" stroke="#7000FF" strokeWidth={2} fill="url(#colorValue)" />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Quick Actions & Recent Transactions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="glass-card p-6 rounded-2xl"
          >
            <h2 className="text-xl font-primary font-bold text-white mb-6">QUICK ACTIONS</h2>
            <div className="space-y-3">
              <Link to="/wallet" data-testid="quick-action-deposit">
                <button className="w-full flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#7000FF]/50 transition-all duration-300">
                  <div className="w-10 h-10 rounded-full bg-[#7000FF]/20 flex items-center justify-center">
                    <ArrowDownRight className="w-5 h-5 text-[#7000FF]" />
                  </div>
                  <div className="text-left">
                    <div className="text-white font-secondary font-semibold">Deposit Crypto</div>
                    <div className="text-gray-400 text-sm">Add funds to your wallet</div>
                  </div>
                </button>
              </Link>

              <Link to="/transfer" data-testid="quick-action-send">
                <button className="w-full flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#00F0FF]/50 transition-all duration-300">
                  <div className="w-10 h-10 rounded-full bg-[#00F0FF]/20 flex items-center justify-center">
                    <Send className="w-5 h-5 text-[#00F0FF]" />
                  </div>
                  <div className="text-left">
                    <div className="text-white font-secondary font-semibold">Send Crypto</div>
                    <div className="text-gray-400 text-sm">Transfer to another wallet</div>
                  </div>
                </button>
              </Link>

              <Link to="/cards" data-testid="quick-action-cards">
                <button className="w-full flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#FFD700]/50 transition-all duration-300">
                  <div className="w-10 h-10 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-[#FFD700]" />
                  </div>
                  <div className="text-left">
                    <div className="text-white font-secondary font-semibold">Get a Card</div>
                    <div className="text-gray-400 text-sm">Purchase Silver or Gold card</div>
                  </div>
                </button>
              </Link>
            </div>
          </motion.div>

          {/* Recent Transactions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="glass-card p-6 rounded-2xl"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-primary font-bold text-white">RECENT ACTIVITY</h2>
              <Link to="/transactions" className="text-[#00F0FF] text-sm font-secondary hover:text-[#00C2CC] transition-colors">
                View all
              </Link>
            </div>
            <div className="space-y-3">
              {transactions.length === 0 ? (
                <p className="text-gray-400 text-center py-8 font-secondary">No transactions yet</p>
              ) : (
                transactions.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        tx.type === 'deposit' ? 'bg-[#00FF94]/20' :
                        tx.type === 'transfer' ? 'bg-[#00F0FF]/20' :
                        tx.type === 'convert' ? 'bg-[#FFD700]/20' :
                        'bg-[#FF0080]/20'
                      }`}>
                        {tx.type === 'deposit' && <ArrowDownRight className="w-4 h-4 text-[#00FF94]" />}
                        {tx.type === 'transfer' && <Send className="w-4 h-4 text-[#00F0FF]" />}
                        {tx.type === 'convert' && <TrendingUp className="w-4 h-4 text-[#FFD700]" />}
                        {tx.type === 'card_purchase' && <CreditCard className="w-4 h-4 text-[#FF0080]" />}
                      </div>
                      <div>
                        <div className="text-white font-secondary text-sm font-semibold capitalize">{tx.type.replace('_', ' ')}</div>
                        <div className="text-gray-400 text-xs">{formatDate(tx.timestamp)}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-mono text-sm">
                        {tx.crypto ? `${tx.amount} ${tx.crypto}` : formatCurrency(tx.amount)}
                      </div>
                      <div className={`text-xs ${
                        tx.status === 'completed' ? 'text-[#00FF94]' :
                        tx.status === 'pending' ? 'text-[#FFD700]' :
                        'text-gray-400'
                      }`}>
                        {tx.status}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
